import {
  PREVENTION_CONTROL_TYPES,
  PREVENTION_WAF_VENDORS,
  WAF_MATCH_OPERATORS,
  WAF_MATCH_TARGETS,
  type PreventionControlSpec,
  type PreventionWafVendor,
  type WafControlSpec,
  type WafMatchCondition,
  type WafMatchTarget,
} from './prevention.schema';

type WafVendorCapabilityProfile = {
  unsupportedTargets: ReadonlySet<WafMatchTarget>;
  forbidsInlineRegexFlags: boolean;
  forbidsLookaroundAndBackrefs: boolean;
};

const EMPTY_TARGETS = new Set<WafMatchTarget>();

const VERCEL_UNSUPPORTED_TARGETS = new Set<WafMatchTarget>([
  WAF_MATCH_TARGETS.BODY,
  WAF_MATCH_TARGETS.QUERY_STRING,
]);

const WAF_VENDOR_CAPABILITY_PROFILES: Record<
  PreventionWafVendor,
  WafVendorCapabilityProfile
> = {
  [PREVENTION_WAF_VENDORS.GENERIC]: {
    unsupportedTargets: EMPTY_TARGETS,
    forbidsInlineRegexFlags: false,
    forbidsLookaroundAndBackrefs: false,
  },
  [PREVENTION_WAF_VENDORS.CLOUDFLARE]: {
    unsupportedTargets: EMPTY_TARGETS,
    forbidsInlineRegexFlags: false,
    forbidsLookaroundAndBackrefs: true,
  },
  [PREVENTION_WAF_VENDORS.AWS_WAFV2]: {
    unsupportedTargets: EMPTY_TARGETS,
    forbidsInlineRegexFlags: true,
    forbidsLookaroundAndBackrefs: true,
  },
  [PREVENTION_WAF_VENDORS.VERCEL]: {
    unsupportedTargets: VERCEL_UNSUPPORTED_TARGETS,
    forbidsInlineRegexFlags: true,
    forbidsLookaroundAndBackrefs: true,
  },
};

const INLINE_REGEX_FLAG_PATTERN = /\(\?[imsux]+\)/;
const LOOKAROUND_PATTERN = /\(\?<?[=!]/;
const BACKREF_PATTERN = /(?:^|[^\\])\\[1-9]/;

export const isWafVendorCapabilityViolationError = (
  error: unknown,
): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }
  const message = error.message;
  return (
    message.includes('does not support') ||
    message.includes('inline regex flag') ||
    message.includes('lookaround') ||
    message.includes('backreference') ||
    message.includes('RE2') ||
    message.includes('regex dialect')
  );
};

export const resolvePreventionWafVendor = (
  vendor: string | null | undefined,
): PreventionWafVendor | null => {
  if (!vendor) {
    return null;
  }
  const parsed = Object.values(PREVENTION_WAF_VENDORS).find(
    (value) => value === vendor,
  );
  return parsed ?? null;
};

export const resolveWafVendorSupportedTargets = (
  vendor: string | null | undefined,
): WafMatchTarget[] => {
  const resolvedVendor = resolvePreventionWafVendor(vendor);
  if (!resolvedVendor) {
    return Object.values(WAF_MATCH_TARGETS);
  }
  const profile = WAF_VENDOR_CAPABILITY_PROFILES[resolvedVendor];
  return Object.values(WAF_MATCH_TARGETS).filter(
    (target) => !profile.unsupportedTargets.has(target),
  );
};

const regexValueNeedsDialectCheck = (
  condition: WafMatchCondition,
): string | null => {
  if (condition.operator === WAF_MATCH_OPERATORS.REGEX) {
    return condition.value?.trim() || null;
  }
  return null;
};

const collectRegexDialectViolations = ({
  condition,
  profile,
  vendor,
}: {
  condition: WafMatchCondition;
  profile: WafVendorCapabilityProfile;
  vendor: PreventionWafVendor;
}): string[] => {
  const value = regexValueNeedsDialectCheck(condition);
  if (!value) {
    return [];
  }

  const violations: string[] = [];
  if (
    profile.forbidsInlineRegexFlags &&
    INLINE_REGEX_FLAG_PATTERN.test(value)
  ) {
    violations.push(
      `${vendor} WAF regex dialect rejects PCRE inline flags like (?i). Use explicit character classes or case variants (and lowercase transform on AWS) instead of (?i)/(?m)/(?s).`,
    );
  }
  if (profile.forbidsLookaroundAndBackrefs) {
    if (LOOKAROUND_PATTERN.test(value)) {
      violations.push(
        `${vendor} WAF regex dialect rejects lookaround assertions ((?=), (?!), (?<=), (?<!)). Rewrite the pattern without lookaround.`,
      );
    }
    if (BACKREF_PATTERN.test(value)) {
      violations.push(
        `${vendor} WAF regex dialect rejects backreferences (\\1, \\2, …). Rewrite the pattern without backreferences.`,
      );
    }
  }
  return violations;
};

export const collectWafVendorCapabilityViolations = ({
  spec,
  vendor,
}: {
  spec: PreventionControlSpec | WafControlSpec;
  vendor: string | null | undefined;
}): string[] => {
  if (spec.controlType !== PREVENTION_CONTROL_TYPES.WAF) {
    return [];
  }
  const resolvedVendor = resolvePreventionWafVendor(vendor);
  if (!resolvedVendor) {
    return [];
  }

  const profile = WAF_VENDOR_CAPABILITY_PROFILES[resolvedVendor];
  const violations: string[] = [];

  const unsupported = [
    ...new Set(
      spec.matchConditions
        .map((condition) => condition.target)
        .filter((target) => profile.unsupportedTargets.has(target)),
    ),
  ];
  if (unsupported.length > 0) {
    violations.push(
      `${resolvedVendor} WAF does not support ${unsupported.join(', ')} match conditions. Use path, method, header, or named query parameters instead.`,
    );
  }

  for (const condition of spec.matchConditions) {
    violations.push(
      ...collectRegexDialectViolations({
        condition,
        profile,
        vendor: resolvedVendor,
      }),
    );
  }

  return [...new Set(violations)];
};
